// Shared offscreen Three.js renderer that produces a static snapshot image (data URL)
// for each unique .glb model. Cards then display the snapshot as a plain <img>, so we
// avoid spawning 13 concurrent WebGL contexts (which browsers cap and which makes the
// shop laggy or blank).

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

const SNAP_SIZE = 480; // rendered at 480x320 for crisp scaling
const SNAP_ASPECT = 1.5;

let _renderer = null;
let _scene = null;
let _camera = null;
let _draco = null;
let _gltfLoader = null;

const _cache = new Map();  // modelUrl -> Promise<dataUrl>
const _pending = [];       // FIFO queue of pending model URLs
let _isProcessing = false;

function _initRenderer() {
  if (_renderer) return;
  _renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  _renderer.setPixelRatio(1.5);
  _renderer.setSize(SNAP_SIZE, Math.round(SNAP_SIZE / SNAP_ASPECT));
  _renderer.setClearColor(0x000000, 0);
  _renderer.outputColorSpace = THREE.SRGBColorSpace;
  _renderer.toneMapping = THREE.ACESFilmicToneMapping;
  _renderer.toneMappingExposure = 1.2;

  _scene = new THREE.Scene();
  _scene.background = null;

  _camera = new THREE.PerspectiveCamera(32, SNAP_ASPECT, 0.1, 100);
  _camera.position.set(2.5, 1.6, 3.4);
  _camera.lookAt(0, 0.15, 0);

  _scene.add(new THREE.AmbientLight(0xaaccff, 1.5));
  const key = new THREE.DirectionalLight(0xffffff, 1.9);
  key.position.set(3, 4, 3);
  _scene.add(key);
  const rim = new THREE.DirectionalLight(0x88aaff, 0.9);
  rim.position.set(-3, 2, -3);
  _scene.add(rim);

  _draco = new DRACOLoader();
  _draco.setDecoderPath("/draco/");
  _gltfLoader = new GLTFLoader();
  _gltfLoader.setDRACOLoader(_draco);
}

function _loadGltf(url) {
  return new Promise((resolve, reject) => {
    _gltfLoader.load(url, (gltf) => resolve(gltf), undefined, (err) => reject(err));
  });
}

async function _snapshot(url) {
  _initRenderer();
  const gltf = await _loadGltf(url);
  const ship = gltf.scene.clone(true);
  // Normalize scale so max dim ~= 2
  const box = new THREE.Box3().setFromObject(ship);
  const size = new THREE.Vector3(); box.getSize(size);
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  const scale = 2.2 / maxDim;
  ship.scale.setScalar(scale);
  const center = new THREE.Vector3(); box.getCenter(center);
  ship.position.sub(center.multiplyScalar(scale));
  ship.rotation.y = Math.PI * 0.25;
  _scene.add(ship);
  _renderer.render(_scene, _camera);
  const dataUrl = _renderer.domElement.toDataURL("image/webp", 0.85);
  _scene.remove(ship);
  ship.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) {
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => m.dispose?.());
    }
  });
  return dataUrl;
}

async function _processQueue() {
  if (_isProcessing) return;
  _isProcessing = true;
  // Pre-fetch the next few .glbs in parallel while the renderer processes them serially.
  // gltf loading is I/O + CPU-heavy (Draco decode); rendering itself is fast (<50ms once loaded).
  const PARALLEL_PREFETCH = 3;
  while (_pending.length > 0) {
    const url = _pending.shift();
    const entry = _cache.get(url);
    if (!entry || entry.resolved) continue;
    // Kick off prefetches for the next N urls WITHOUT awaiting — GLTFLoader will cache internally
    for (let i = 0; i < Math.min(PARALLEL_PREFETCH, _pending.length); i++) {
      const nextUrl = _pending[i];
      // Fire and forget — we just want the decode work happening in parallel
      _gltfLoader.load(nextUrl, () => {}, undefined, () => {});
    }
    try {
      const dataUrl = await _snapshot(url);
      entry.resolved = true;
      entry.resolve(dataUrl);
    } catch (err) {
      entry.resolved = true;
      entry.reject(err);
    }
  }
  _isProcessing = false;
}

/**
 * Returns a Promise resolving to a data-URL snapshot image of the given .glb.
 * Snapshots are cached in memory so repeated calls are free after the first.
 */
export function getSkinSnapshot(modelUrl) {
  if (!modelUrl) return Promise.reject(new Error("no model url"));
  if (_cache.has(modelUrl)) return _cache.get(modelUrl).promise;
  let resolve, reject;
  const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
  const entry = { promise, resolve, reject, resolved: false };
  _cache.set(modelUrl, entry);
  _pending.push(modelUrl);
  _processQueue();
  return promise;
}
